/* Upsell section component for checkout page.
   Updated: Product cards now match the Aanplant en verzorging catalogue cards.
   Displays simple products from a specific category for quick add-to-cart. */

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useCart } from '@/contexts/CartContext';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import { Package, ShoppingCart } from 'lucide-react';
import { motion } from 'framer-motion';

interface UpsellProduct {
  id: string;
  name: string;
  scientific_name?: string;
  simple_price: number;
  btw_percentage: number;
  image_url?: string;
  base_slug: string;
  description?: string;
}

interface UpsellSectionProps {
  categorySlug: string;
  title?: string;
  subtitle?: string;
}

export default function UpsellSection({
  categorySlug,
  title = 'Vergeet niet erbij te bestellen',
  subtitle = 'Maak je bestelling compleet',
}: UpsellSectionProps) {
  const [products, setProducts] = useState<UpsellProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const [addingProduct, setAddingProduct] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
  }, [categorySlug]);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const { data: category } = await supabase
        .from('product_categories')
        .select('id')
        .eq('slug', categorySlug)
        .maybeSingle();

      if (!category) return;

      const { data: productsData, error } = await supabase
        .from('products')
        .select('id, name, scientific_name, simple_price, btw_percentage, image_url, base_slug, description')
        .eq('category_id', category.id)
        .eq('is_simple_product', true)
        .eq('active', true)
        .order('name');

      if (error) throw error;

      const resolvedProducts = productsData || [];
      if (resolvedProducts.length > 0) {
        const ids = resolvedProducts.map((p) => p.id);
        const { data: primaryImages } = await supabase
          .from('product_images')
          .select('product_id, storage_path, uses_new_bucket')
          .in('product_id', ids)
          .is('variant_id', null)
          .eq('is_primary', true);

        if (primaryImages && primaryImages.length > 0) {
          const imageMap = new Map(
            primaryImages.map((img) => [img.product_id, getProductImageUrl(img.storage_path, img.uses_new_bucket)]),
          );
          for (const product of resolvedProducts) {
            if (imageMap.has(product.id)) {
              product.image_url = imageMap.get(product.id)!;
            }
          }
        }
      }

      setProducts(resolvedProducts);
    } catch (error) {
      console.error('Error fetching upsell products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product: UpsellProduct) => {
    setAddingProduct(product.id);
    try {
      const priceInclBTW = calculatePriceInclBTW(product.simple_price, product.btw_percentage);
      await addItem({
        product_id: product.id,
        product_variant_id: null,
        product_name: product.name,
        variant_size: product.name,
        unit_price: priceInclBTW,
        image_url: product.image_url,
        slug: product.base_slug,
        quantity: 1,
        is_simple_product: true,
      });
    } catch (error) {
      console.error('Error adding product to cart:', error);
    } finally {
      setAddingProduct(null);
    }
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(price);

  if (loading || products.length === 0) return null;

  return (
    <div className="w-full">
      {/* Section header — matches homepage pattern */}
      <div className="mb-10 text-center">
        <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Maak je bestelling compleet</p>
        <h2 className="text-4xl tracking-[-0.035em] text-[#1e2b22] md:text-5xl">{title}</h2>
        <p className="mx-auto mt-3 max-w-xl text-base leading-7 text-[#5a6552] sm:leading-8">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
        {products.map((product, index) => {
          const priceInclBTW = calculatePriceInclBTW(product.simple_price, product.btw_percentage);

          return (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="group relative block h-[300px] cursor-pointer overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl"
              style={{
                borderTopLeftRadius: index % 2 === 0 ? '2.5rem' : 0,
                borderBottomRightRadius: index % 2 === 0 ? '2.5rem' : 0,
                borderTopRightRadius: index % 2 === 0 ? 0 : '2.5rem',
                borderBottomLeftRadius: index % 2 === 0 ? 0 : '2.5rem',
              }}
            >
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-100">
                  <Package className="h-10 w-10 text-gray-300" />
                  <span className="text-xs font-medium text-gray-400">Foto volgt snel!</span>
                </div>
              )}

              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0) 65%)',
                }}
              />

              <div className="absolute right-3 top-3 z-10 rounded-[5px] bg-[#1e2b22] px-2.5 py-1.5 text-xs font-bold text-white shadow-[0_2px_8px_rgba(0,0,0,0.3),0_0_12px_rgba(255,255,255,0.7)]">
                {formatPrice(priceInclBTW)}
              </div>

              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-2 px-4 py-4">
                <div className="min-w-0 flex-1">
                  {product.scientific_name && (
                    <p className="mb-0.5 line-clamp-1 text-xs italic text-white/70">{product.scientific_name}</p>
                  )}
                  <h3 className="line-clamp-2 text-sm font-bold leading-tight text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.6)] transition-all duration-200 group-hover:underline group-hover:decoration-2 group-hover:decoration-[#5a6552]">
                    {product.name}
                  </h3>
                </div>

                <button
                  onClick={() => handleAddToCart(product)}
                  disabled={addingProduct === product.id}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center bg-[#5a6552] shadow-lg transition-colors hover:bg-[#1e2b22] disabled:opacity-50"
                  style={{ borderTopLeftRadius: '12px', borderBottomRightRadius: '12px' }}
                  aria-label={`${product.name} toevoegen aan winkelwagen`}
                >
                  {addingProduct === product.id ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <ShoppingCart className="h-4 w-4 text-white" />
                  )}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
